import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import GobActivity, {GobHourActivity, TopToolChange} from "./gobActivity";
import JobRunActivity from "./jobRunActivity";
import HourActivity from "./hourActivity";
import ToolActivity from "./toolActivity";
import {DefectType} from "../defectType";
import * as moment from "moment";
import * as _ from "lodash";
import {apsEvents} from "../../shared/dto/aps.events";
import DefectActivity from "./defectActivity";
import {DefectTypesAndClassifications} from "../../services/defects/defectType.service";
import { job } from "cron";

export interface LatestTool {
  name: string;
  toolChange: ToolActivity;
}

export interface LatestTools {
  [id: number]: LatestTool;
}

export interface SectionActivity {
  name: string;
  sectionNumber: number;
  machine: MachineActivity;
  gobs: GobActivity[];
  latestTools: LatestTools;
}

export interface MachineActivity extends apsViewModels.MachineActivity {
  shop: ShopActivity;
  sections: SectionActivity[];
  gobs: GobActivity[];
  machineLevelActivity: GobActivity;
  latestTools: LatestTools;
}

export class ShopActivity {
  id: string;
  name: string;
  timeZoneId: string;
  sections: SectionActivity[];
  gobs: GobActivity[];
  machines: MachineActivity[];
  hoursAndJobs: (HourActivity|JobRunActivity)[];
  hours: HourActivity[];
  jobRuns: JobRunActivity[];
  allActivity: GobActivity[];
  private maxNumberOfSections: number;
  private maxNumberOfGobs: number;
  private defectTypes: DefectType[];
  private tools: apsViewModels.ToolAndType[];
  numberOfColumns: number;
  private numberOfUnlockedHours: number;
  private userProfileData: apsViewModels.UserProfileData;
  private jobs: apsViewModels.JobItem[];
  private jobProcess: apsViewModels.JobProcess[];
  private eightyHrTools: string;
  private oneSixtyHrTools: string;

  constructor(data: apsViewModels.ShopActivity,
              defectTypesAndClassifications: DefectTypesAndClassifications,
              userProfileData: apsViewModels.UserProfileData,
              toolsAndTypes: apsViewModels.ToolAndType[],
              jobs: apsViewModels.JobItem[],
              jobProcess: apsViewModels.JobProcess[],
              eightyHrTools: string,
              oneSixtyHrTools: string) {
    this.userProfileData = userProfileData;
    this.numberOfColumns = 30;
    this.id = data.id;
    this.name = data.name;
    this.timeZoneId = data.timeZoneId;
    this.maxNumberOfSections = 0;
    this.maxNumberOfGobs = 0;
    this.defectTypes = defectTypesAndClassifications.types;
    this.tools = toolsAndTypes;
    this.jobs = jobs;
    this.jobProcess = jobProcess;
    this.eightyHrTools = eightyHrTools;
    this.oneSixtyHrTools = oneSixtyHrTools;
    this.initializeHours(data);
    this.jobRuns = _.map(data.jobRuns, (jobRun) => new JobRunActivity(jobRun));

    _.forEach(this.jobRuns, (jobRun) => {
      if (jobRun.numberOfSections > this.maxNumberOfSections) {
        this.maxNumberOfSections = jobRun.numberOfSections;
      }
      if (jobRun.numberOfGobs > this.maxNumberOfGobs) {
        this.maxNumberOfGobs = jobRun.numberOfGobs;
      }

      this.updateHoursFromJobRun(jobRun);
    });

    this.initializeHoursAndJobs();

    this.machines = [];
    this.sections = [];
    this.gobs = [];

    let activeJobRuns = this.jobRuns.filter(x => !x.endDate);
    let currentJobRun = activeJobRuns.length > 0 ? activeJobRuns[0] : null;
    _.forEach(data.machines, (machine: apsViewModels.MachineActivity) => {
      this.initializeMachine(machine, currentJobRun);
    });
    this.initializeAllActivity();

    this.addDefects(data.defects);
    this.addToolChanges(data.toolChanges);
    this.addDowntime(data.downtime);
    this.addQualifications(data.qualifications);
    this.addCheckLists(data.checkLists);
  }

  updateHours() {
    var startTime = moment().startOf('hour').toDate();
    var endTime = moment(startTime).add(1, 'hours').toDate();
    var lastUnlockedHour = moment(startTime)
      .subtract(this.numberOfUnlockedHours - 1, 'hours').toDate();
    var index = 0;
    while (startTime.getTime() >= this.hours[0].endTime.getTime()) {
      this.hours.splice(index, 0, new HourActivity(startTime,
        endTime, (startTime.getTime() < lastUnlockedHour.getTime()), this.timeZoneId));
      _.forEach(this.allActivity, (a: GobActivity) => a.insertHour(index, this.hours[index]));
      endTime = startTime;
      startTime = moment(startTime).subtract(1, 'hours').toDate();
      index++;
    }
    _.forEach(
      _.filter(this.hours, (h: HourActivity) => !h.overridden &&
        !h.isLocked && h.startTime.getTime() < lastUnlockedHour.getTime()),
      (h: HourActivity) => h.isLocked = true
    );
    var lastHour = moment().startOf('hour').subtract(23, 'hours').toDate().getTime();
    _.remove(this.hours, h => h.endTime.getTime() <= lastHour);
    _.forEach(this.jobRuns, (jobRun) => this.updateHoursFromJobRun(jobRun));
    this.initializeHoursAndJobs();
  }

  private initializeHours(data: apsViewModels.ShopActivity) {
    var hours = [];
    this.numberOfUnlockedHours = data.numberOfUnlockedHours;
    var lastUnlockedHour = moment().startOf('hour')
      .subtract(data.numberOfUnlockedHours - 1, 'hours').toDate();
    var startTime = data.lastHour;
    var endTime = moment(startTime).add(1, 'hours').toDate();
    while (startTime.getTime() >= data.firstHour.getTime()) {
      hours.push(new HourActivity(startTime,
        endTime, (startTime.getTime() < lastUnlockedHour.getTime()), data.timeZoneId));
      endTime = startTime;
      startTime = moment(startTime).subtract(1, 'hours').toDate();
    }
    this.hours = hours;
    _.forEach(data.lockedHours, (hour: apsViewModels.HourLockActivity) => {
      this.lockUnlockHourFromEvent(hour.hour, hour.isLocked);
    });
  }

  private initializeAllActivity() {
    var all = [];
    _.forEach(this.machines, (machine: MachineActivity) => {
      all.push(machine.machineLevelActivity);
      all = all.concat(machine.gobs);
    });
    this.allActivity = all;
  }

  private addCheckLists(checkLists: apsViewModels.CheckListActivity[]): void {
    _.forEach(checkLists, (checkList: apsViewModels.CheckListActivity) => {
      var index = this.getHourIndex(checkList.scheduledOn);
      if (index === null) {
        return;
      }

      this.hours[index].addOrUpdateCheckList(checkList);
    });
  }

  private addDefects(defects: apsViewModels.DefectActivity[]): void {
    _.forEach(defects, (defect: apsViewModels.DefectActivity) => {
      var gobs = this.findGobs(defect.isMachineId, defect.section, defect.gob, true);
      _.forEach(gobs, (gob: GobActivity) => gob.addDefect(defect));
    });
  }

  private addToolChanges(toolChanges: apsViewModels.ToolActivity[]): void {
    _.forEach(toolChanges, (toolChange: apsViewModels.ToolActivity) => {
      var gobs = this.findGobs(toolChange.isMachineId, toolChange.section, toolChange.gob, true);
      _.forEach(gobs, (gob: GobActivity) => gob.addToolChange(toolChange));
    });
  }

  private addDowntime(downtimes: apsViewModels.DowntimeActivity[]): void {
    _.forEach(downtimes, (downtime: apsViewModels.DowntimeActivity) => {
      var gobs = this.findGobs(downtime.isMachineId, downtime.section);
      _.forEach(gobs, (gob: GobActivity) => gob.addDowntime(downtime));
    });
  }

  private addQualifications(qualifications: apsViewModels.QualificationActivity[]): void {
    _.forEach(qualifications, (qualification: apsViewModels.QualificationActivity) => {
      var gobs = this.findGobs(qualification.isMachineId, qualification.section, qualification.gob);
      _.forEach(gobs, (gob: GobActivity) => gob.addQualification(qualification));
    });
  }

  private updateHoursFromJobRun(jobRun: JobRunActivity) {
    if (!jobRun.endDate || this.isFuture(jobRun.endDate)) {
      return;
    }

    var hourIndex = this.getHourIndex(jobRun.endDate);
    if (hourIndex === null) {
      return;
    }

    var hour = this.hours[hourIndex];
    if (moment.utc(jobRun.endDate).isSame(
        moment.utc(hour.startTime))) {
      return;
    }

    var newHour = new HourActivity(
      jobRun.endDate,
      hour.endTime,
      hour.isLocked,
      this.timeZoneId);
    this.hours.splice(hourIndex, 0, newHour);
    hour.endTime = jobRun.endDate;
  }

  private initializeHoursAndJobs() {
    this.hoursAndJobs = [];
    var currentJob = _.last(this.jobRuns);
    var noEndTime = !currentJob || !(currentJob.endDate);
    _.forEach(this.hours, (hour: HourActivity) => {
      if (currentJob && hour.startTime.getTime() >= currentJob.startDate.getTime()
        && (noEndTime || hour.startTime.getTime() < currentJob.endDate.getTime())) {
        hour.jobRun = currentJob;
        currentJob.hours.push(hour);
      }
      this.hoursAndJobs.push(hour);
      var jobRun = _.find(this.jobRuns, {endDate: hour.startTime});
      if (jobRun) {
        this.hoursAndJobs.push(jobRun);
        currentJob = jobRun;
        noEndTime = false;
      }
    });
  }

  displayHoursAndJobs(): (HourActivity|JobRunActivity)[] {
    return _.take(this.hoursAndJobs, this.numberOfColumns);
  }

  private createLatestTools(typeName: string): LatestTools {
    var latestTools = <LatestTools>{};
    _.filter(this.tools, {typeName: typeName}).forEach((tool) => {
      latestTools[tool.id] = {
        name: tool.name,
        toolChange: undefined
      };
    });
    return latestTools;
  }

  private initializeMachine(machine: apsViewModels.MachineActivity, jobRunActivity: JobRunActivity | null) {
    var machineActivity: MachineActivity = {
      id: machine.id,
      name: machine.name,
      number: machine.number,
      shop: this,
      sections: [],
      gobs: [],
      machineLevelActivity: undefined,
      latestTools: this.createLatestTools('Machine')
    };
    machineActivity.machineLevelActivity = this.createMachineLevelActivity(machineActivity);
    this.machines.push(machineActivity);

    this.initializeSections(machineActivity, jobRunActivity);
  }

  private createMachineLevelActivity(machineActivity: MachineActivity): GobActivity {
    var sectionZero: SectionActivity = {
      name: '',
      sectionNumber: undefined,
      machine: machineActivity,
      gobs: [],
      latestTools: this.createLatestTools('Variable')
    };

    var activity = new GobActivity(undefined,
      this.maxNumberOfGobs, sectionZero, this.tools, this.userProfileData, this.defectTypes, this.jobs, this.jobProcess, this.eightyHrTools, this.oneSixtyHrTools );
    sectionZero.gobs.push(activity);
    return activity;
  }

  private initializeSections(machineActivity: MachineActivity, jobRunActivity: JobRunActivity | null) {
    _.forEach(_.range(1, this.maxNumberOfSections + 1), (section) => {
      var sectionActivity: SectionActivity = {
        name: (machineActivity.number === 2
          ? (section + this.maxNumberOfSections)
          : section).toString(),
        sectionNumber: section,
        machine: machineActivity,
        gobs: [],
        latestTools: this.createLatestTools('Variable')
      };

      machineActivity.sections.push(sectionActivity);
      this.sections.push(sectionActivity);

      this.initializeGobs(machineActivity, sectionActivity);
    });
  }

  private initializeGobs(machineActivity: MachineActivity, sectionActivity: SectionActivity) {
    _.forEach(_.range(1, this.maxNumberOfGobs + 1), (gob) => {
      var gobActivity = new GobActivity(gob,
        this.maxNumberOfGobs, sectionActivity, this.tools, this.userProfileData, this.defectTypes, this.jobs, this.jobProcess, this.eightyHrTools, this.oneSixtyHrTools);

      sectionActivity.gobs.push(gobActivity);
      machineActivity.gobs.push(gobActivity);
      this.gobs.push(gobActivity);
    });
  }

  addDefectFromEvent(event: apsEvents.DefectAdded): void {
    if (this.getHourIndex(event.timeFound) === null) {
      return;
    }

    var gobs = this.findGobs(event.isMachineId, event.section, event.gob, true);
    if (gobs.length === 0) {
      throw 'Unable to find gobs for event: ' + event;
    }

    var defectType = _.find(this.defectTypes, {id: event.defectTypeId});
    if (!defectType) {
      throw 'Unable to find defect type for event: ' + event;
    }

    var defectActivity = _.assign<apsViewModels.DefectActivity>({}, event);
    if (!defectActivity.createdOn) {
       defectActivity.createdOn = event.eventDate;
    }

    if (defectActivity.comments.length > 0) {
      defectActivity.comments[0].createdOn = event.comments[0].eventDate;
    }
    _.forEach(gobs, (gob: GobActivity) => {
      gob.addDefect(defectActivity);
    });
  }

  deleteDefectFromEvent(event: apsEvents.DefectDeleted): void {
    var index = this.getHourIndex(event.timeFound);
    if (index === null) {
      return;
    }

    var gobs = this.findGobs(event.isMachineId, event.section, event.gob, true);
    if (gobs.length === 0) {
      throw 'Unable to find gobs for event: ' + event;
    }

    _.forEach(gobs, (gob: GobActivity) => {
      var defect = _.find(gob.hours[index].activity, {id: event.id});
      if (defect) {
        _.remove(gob.hours[index].activity, defect);
      }

      defect = _.find(gob.defects, {id: event.id});
      if (defect) {
        _.remove(gob.defects, defect);
      }
    });
  }

  addDowntimeFromEvent(event: apsEvents.DowntimeAdded): void {
    if (this.getHourIndex(event.downtimeStart) === null) {
      return;
    }

    var gobs = this.findGobs(event.isMachineId, event.section);
    if (gobs.length === 0) {
      throw 'Unable to find gobs for event: ' + event;
    }

    var downtimeActivity = _.assign<apsViewModels.DowntimeActivity>({}, event);
    downtimeActivity.comments = [];
    _.forEach(gobs, (gob: GobActivity) => {
      gob.addDowntime(downtimeActivity);

      _.forEach(event.comments, (commentAdded: apsEvents.DowntimeCommentAdded) => {
        gob.addDowntimeComment(commentAdded);
      });
    });
  }

  deleteDowntimeFromEvent(event: apsEvents.DowntimeDeleted): void {
    var index = this.getHourIndex(event.downtimeStart);
    if (index === null) {
      return;
    }

    var gobs = this.findGobs(event.isMachineId, event.section);
    if (gobs.length === 0) {
      throw 'Unable to find gobs for event: ' + event;
    }

    _.forEach(gobs, (gob: GobActivity) => {
      var downtime = _.find(gob.hours[index].activity, {id: event.id});
      if (downtime) {
        _.remove(gob.hours[index].activity, downtime);
      }
    });
  }

  addQualificationFromEvent(event: apsEvents.QualificationAdded): void {
    if (this.getHourIndex(event.qualificationTime) === null) {
      return;
    }

    var gobs = this.findGobs(event.isMachineId, event.section, event.gob);
    if (gobs.length === 0) {
      throw 'Unable to find gobs for event: ' + event;
    }

    _.forEach(gobs, (gob: GobActivity) => {
      gob.addQualification(event);
    });
  }

  deleteQualificationFromEvent(event: apsEvents.QualificationDeleted): void {
    if (this.getHourIndex(event.qualificationTime) === null) {
      return;
    }

    var gobs = this.findGobs(event.isMachineId, event.section, event.gob);
    if (gobs.length === 0) {
      throw 'Unable to find gobs for event: ' + event;
    }

    _.forEach(gobs, (gob: GobActivity) => {
      gob.deleteQualification(event);
    });
  }

  addDefectCommentFromEvent(commentEvent: apsEvents.DefectCommentAdded): void {
    var index = this.getHourIndex(commentEvent.timeFound);
    if (index === null) {
      return;
    }

    var gobs = this.findGobs(commentEvent.isMachineId, commentEvent.section, commentEvent.gob, true);
    if (gobs.length === 0) {
      throw 'Unable to find gobs for event: ' + event;
    }

    var comment = _.assign<apsViewModels.DefectDetailsComment>({}, commentEvent);
    comment.createdOn = commentEvent.eventDate;
    _.forEach(gobs, (gob: GobActivity) => {
      var defect = <DefectActivity>_.find(gob.hours[index].activity, {id: commentEvent.defectId});
      if (defect) {
        defect.comments.push(comment);
      }
    });
  }

  addDefectHeldPalletsFromEvent(addHeldPallets: apsEvents.DefectHeldPalletsAdded): void {
    var index = this.getHourIndex(addHeldPallets.timeFound);
    if (index === null) {
      return;
    }

    var gobs = this.findGobs(addHeldPallets.isMachineId, addHeldPallets.section, addHeldPallets.gob, true);
    if (gobs.length === 0) {
      throw 'Unable to find gobs for event: ' + event;
    }

    var heldPallets = _.assign<apsViewModels.DefectDetailsHeldPallet>({}, addHeldPallets);
    _.forEach(gobs, (gob: GobActivity) => {
      var defect = <DefectActivity>_.find(gob.hours[index].activity, { id: addHeldPallets.defectId });
      if (defect) {
        var heldPallet = _.find<apsViewModels.DefectDetailsHeldPallet>(defect.heldPallets, { id: addHeldPallets.id });
        if (heldPallet) {
          return;
        }
        defect.heldPallets.push(heldPallets);
      }
    });
  }

  deleteDefectHeldPalletsFromEvent(deleteHeldPallets: apsEvents.DefectHeldPalletsDeleted): void {
    var index = this.getHourIndex(deleteHeldPallets.timeFound);
    if (index === null) {
      return;
    }

    var gobs = this.findGobs(deleteHeldPallets.isMachineId, deleteHeldPallets.section, deleteHeldPallets.gob, true);
    if (gobs.length === 0) {
      throw 'Unable to find gobs for event: ' + event;
    }

    _.forEach(gobs, (gob: GobActivity) => {
      var defect = <DefectActivity>_.find(gob.hours[index].activity, { id: deleteHeldPallets.defectId });
      if (defect) {
        var heldPallet = _.find(defect.heldPallets, { id: deleteHeldPallets.id });
        if (!heldPallet) {
          return;
        }
        _.remove(defect.heldPallets, heldPallet);
      }
    });
  }

  addToolChangeFromEvent(toolChangeEvent: apsEvents.ToolChangeAdded): void {
    if (this.getHourIndex(toolChangeEvent.timeChanged) === null) {
      return;
    }

    _.forEach(toolChangeEvent.toolChanges, (toolChange: apsEvents.ToolChangeDetailAdded) => {
      var gobs = this.findGobs(toolChangeEvent.isMachineId, toolChange.section, toolChange.gob, true);
      if (gobs.length === 0) {
        throw 'Unable to find gobs for event: ' + toolChange;
      }

      var tool = _.find(this.tools, {id: toolChange.toolId});
      if (!tool) {
        throw 'Unable to find tool for event: ' + toolChange;
      }

      _.forEach(gobs, (gob: GobActivity) => {
        gob.addToolChange({
          id: toolChange.id,
          shopId: toolChangeEvent.shopId,
          isMachineId: toolChangeEvent.isMachineId,
          section: toolChange.section,
          gob: toolChange.gob,
          gobName: toolChange.gobName,
          unlistedDefect: toolChange.unlistedDefect,
          defectId: toolChange.defectId,
          defectTypeId: toolChange.defectTypeId,
          defectTypeName: toolChange.defectTypeName,
          duration: toolChangeEvent.duration,
          timeChanged: toolChangeEvent.timeChanged,
          toolNumber: toolChange.toolNumber,
          toolId: toolChange.toolId,
          toolName: tool.name,
          toolIsMold: tool.isMold,
          reason: toolChange.reason,
          otherReason: toolChange.otherReason,
          toolChangeId: toolChangeEvent.id,
          createdByFullName: toolChangeEvent.createdByFullName,
          comment: toolChange.comment,
          toolType: tool.type,
          rootComments: _.map(toolChangeEvent.rootComments, (comment: apsEvents.ToolChangeCommentAdded) => {
            return {
              id: comment.id,
              toolChangeId: toolChangeEvent.id,
              comment: comment.comment,
              createdOn: toolChangeEvent.eventDate,
              createdByFullName: toolChangeEvent.createdByFullName
            };
          })
        });
      });
    });
  }

  private editToolChangeForGob(event: apsEvents.ToolChangeEdited,
                               gob: GobActivity,
                               gobHour: GobHourActivity) {
    if (!event || !gob || !gobHour) {
      return;
    }

    var toolChange = <TopToolChange>_.find(gobHour.activity, {id: event.id});
    var toolChanges: ToolActivity[] = [];
    if (toolChange) {
      toolChange.timeChanged = event.timeChanged;
      toolChanges = _.filter(toolChange.tools, tc =>
        (tc.section || 0) === (gob.section.sectionNumber || 0)
        && (((tc.gob || 0) === (gob.gobNumber || 0)) || ((tc.gob || 0) === 0)));
    }

    var eventChanges: apsEvents.ToolChangeDetailAdded[] = _.filter(event.toolChanges, tc =>
      (tc.section || 0) === (gob.section.sectionNumber || 0)
      && (((tc.gob || 0) === (gob.gobNumber || 0)) || ((tc.gob || 0) === 0)));

    if (toolChanges.length === 0 && eventChanges.length === 0) {
      return;
    }

    _.forEach(eventChanges, (ec: apsEvents.ToolChangeDetailAdded) => {
      var detail = _.find(toolChanges, {id: ec.id});
      if (detail) {
        detail.comment = ec.comment;
        detail.defectId = ec.defectId;
        detail.defectTypeName = ec.defectTypeName;
        detail.duration = event.duration;
        detail.otherReason = ec.otherReason;
        detail.reason = ec.reason;
        detail.timeChanged = event.timeChanged;
        detail.toolNumber = ec.toolNumber;
      } else {
        var tool = _.find(this.tools, {id: ec.toolId});
        if (!tool) {
          throw 'Unable to find tool for event: ' + ec;
        }

        var comments: apsViewModels.ToolChangeDetailsComment[] = [];
        _.forEach(event.rootComments, (c: apsEvents.ToolChangeCommentAdded) => {
          comments.push({
            id: c.id,
            toolChangeId: c.toolChangeId,
            comment: c.comment,
            createdOn: c.eventDate,
            createdByFullName: c.createdByFullName
          });
        });

        gob.addToolChange({
          id: ec.id,
          comment: ec.comment,
          createdByFullName: event.createdByFullName,
          unlistedDefect: ec.unlistedDefect,
          defectId: ec.defectId,
          defectTypeName: ec.defectTypeName,
          defectTypeId: ec.defectTypeId,
          duration: event.duration,
          gob: ec.gob,
          gobName: ec.gobName,
          isMachineId: event.isMachineId,
          otherReason: ec.otherReason,
          reason: ec.reason,
          rootComments: comments,
          section: ec.section,
          shopId: event.shopId,
          timeChanged: event.timeChanged,
          toolChangeId: event.id,
          toolId: ec.toolId,
          toolIsMold: ec.toolIsMold,
          toolName: ec.toolName,
          toolNumber: ec.toolNumber,
          toolType: tool.type
        });
      }
    });

    _.forEach(toolChanges, (tc: ToolActivity) => {
      if (!_.some(eventChanges, {id: tc.id})) {
        gob.removeToolChange(tc, toolChange, gobHour);
      }
    });
  }

  editToolChangeFromEvent(event: apsEvents.ToolChangeEdited): void {
    var index = this.getHourIndex(event.timeChanged);
    if (index === null) {
      return;
    }

    var machine = _.find(this.machines, {id: event.isMachineId});
    if (machine === null) {
      return;
    }

    this.editToolChangeForGob(event, machine.machineLevelActivity, machine.machineLevelActivity.hours[index]);
    _.forEach(machine.gobs, (gob: GobActivity) => {
      this.editToolChangeForGob(event, gob, gob.hours[index]);
    });
  }

  deleteToolChangeFromEvent(event: apsEvents.ToolChangeDeleted): void {
    var index = this.getHourIndex(event.timeChanged);
    if (index === null) {
      return;
    }

    _.forEach(event.toolChanges, (toolChange: apsEvents.ToolChangeDeletedDetail) => {
      var gobs = this.findGobs(event.isMachineId, toolChange.section, toolChange.gob, true);
      if (gobs.length === 0) {
        throw 'Unable to find gobs for event: ' + toolChange;
      }

      _.forEach(gobs, (gob: GobActivity) => {
        var tc = <TopToolChange> _.find(gob.hours[index].activity, {id: event.id});
        if (tc) {
          // _.remove(gob.hours[index].activity, tc);

          tc.tools.forEach(tool => {
            gob.removeToolChange(tool, tc, gob.hours[index]);
          });
        }
      });
    });
  }

  deleteToolChangeDefectFromEvent(event: apsEvents.ToolChangeDefectDeleted): void {
    var index = this.getHourIndex(event.timeChanged);
    if (index === null) {
      return;
    }

    var gobs = this.findGobs(event.isMachineId, event.section, event.gob, true);
    if (gobs.length === 0) {
      throw 'Unable to find gobs for event: ' + event;
    }

    _.forEach(gobs, (gob: GobActivity) => {
      var tc = <TopToolChange> _.find(gob.hours[index].activity, {id: event.id});
      if (tc) {
        var detail = _.find(tc.tools, {id: event.toolChangeDetailId});
        if (detail) {
          detail.defectId = null;
          detail.unlistedDefect = true;
        }
      }
    });
  }

  addToolChangeCommentFromEvent(event: apsEvents.ToolChangeCommentAdded): void {
    var index = this.getHourIndex(event.timeChanged);
    if (index === null) {
      return;
    }

    var gobs = this.findGobs(event.isMachineId, event.section, event.gob, true);
    if (gobs.length === 0) {
      throw 'Unable to find gobs for event: ' + event;
    }

    var comment = _.assign<apsViewModels.ToolChangeDetailsComment>({}, event);
    comment.id = event.id;
    comment.createdOn = event.eventDate;
    _.forEach(gobs, (gob: GobActivity) => {
      var tc = <TopToolChange>_.find(gob.hours[index].activity, {id: event.toolChangeId});
      if (tc) {
        var existing = _.find(tc.comments, { id: comment.id});
        if (!existing) {
          tc.comments.push(comment);
        }
      }
    });
  }

  private lockUnlockHourFromEvent(startTime: Date, lock: boolean) {
    var hour = _.find(this.hours, {startTime: startTime});
    if (hour) {
      hour.isLocked = lock;
      hour.overridden = true;
    }
  }

  lockHourFromEvent(event: apsEvents.HourLocked): void {
    this.lockUnlockHourFromEvent(event.hour, true);
  }

  unlockHourFromEvent(event: apsEvents.HourUnlocked): void {
    this.lockUnlockHourFromEvent(event.hour, false);
  }

  addDowntimeDurationFromEvent(event: apsEvents.DowntimeDurationAdded): void {
    if (this.getHourIndex(event.downtimeStart) === null) {
      return;
    }

    var gobs = this.findGobs(event.isMachineId, event.section);
    if (gobs.length === 0) {
      throw 'Unable to find gobs for event: ' + event;
    }

    _.forEach(gobs, function (gob: GobActivity) {
      gob.addDowntimeDuration(event);
    });
  }

  updateDefectMoldRejectFromEvent(event: apsEvents.DefectMoldRejectUpdated): void {
    var index = this.getHourIndex(event.timeFound);
    if (index === null) {
      return;
    }

    var gobs = this.findGobs(event.isMachineId, event.section, event.gob);
    if (gobs.length === 0) {
      throw 'Unable to find gobs for event: ' + event;
    }

    _.forEach(gobs, (gob: GobActivity) => {
      var defect = <DefectActivity>_.find(gob.hours[index].activity, { id: event.defectId });
      if (defect) {
        defect.setMoldReject(event);
      }
    });
  }
  updateDefectEquipFailureFromEvent(event: apsEvents.DefectEquipFailureUpdated): void {
    var index = this.getHourIndex(event.timeFound);
    if (index === null) {
      return;
    }
    var machine = _.find(this.machines, {id: event.isMachineId});
    var machineDefect = <DefectActivity>_.find(machine.machineLevelActivity.defects, {id: event.defectId});
    if (machineDefect){
        machineDefect.setEquipFailure(event);
    }else {
      var gobs = this.findGobs(event.isMachineId, event.section, event.gob);
      if (gobs.length === 0) {
        throw 'Unable to find gobs for event: ' + event;
      }
  
      _.forEach(gobs, (gob: GobActivity) => {
        var defect = <DefectActivity>_.find(gob.hours[index].activity, { id: event.defectId });
        if (defect) {
          defect.setEquipFailure(event);
        }
      });
    }
  }
  getHourIndex(date: Date): number {
    for (var i = 0; i < this.hours.length; i++) {
      if (this.hours[i].withinHour(date)) {
        return i;
      }
    }
    return null;
  }

  private isFuture(date: Date): boolean {
    return date.getTime() >= this.hours[0].endTime.getTime();
  }

  private findGobs(isMachineId: string,
                   sectionNumber?: number,
                   gobNumber?: number,
                   includeMachineLevel?: boolean): GobActivity[] {
    var machine = _.find(this.machines, {id: isMachineId});
    if (!machine) {
      return [];
    }

    if ((sectionNumber || 0) === 0) {
      if (includeMachineLevel) {
        return [machine.machineLevelActivity];
      }

      if ((gobNumber || 0) === 0) {
        return machine.gobs;
      } else {
        var gobs = [];
        _.forEach(machine.sections, function(section) {
          var gob = _.find(section.gobs, {gobNumber: gobNumber});
          if (gob !== undefined) {
            gobs.push(gob);
          }
        });
        return gobs;
      }
    }

    var section = _.find(machine.sections, {sectionNumber: sectionNumber});
    if (!section) {
      return [];
    }

    if ((gobNumber || 0) === 0) {
      return section.gobs;
    }

    var gob = _.find(section.gobs, {gobNumber: gobNumber});
    if (!gob) {
      return [];
    }
    return [gob];
  }

  addDowntimeCommentFromEvent(event: apsEvents.DowntimeCommentAdded): void {
    if (this.getHourIndex(event.downtimeStart) === null) {
      return;
    }

    var gobs = this.findGobs(event.isMachineId, event.section);
    if (gobs.length === 0) {
      throw 'Unable to find gobs for event: ' + event;
    }

    _.forEach(gobs, function (gob: GobActivity) {
      gob.addDowntimeComment(event);
    });
  }

  checkListActivityFromEvent(event: apsViewModels.CheckListActivity): void {
    var index = this.getHourIndex(event.scheduledOn);
    if (index === null) {
      return;
    }

    this.hours[index].addOrUpdateCheckList(event);
  }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--5!./src/app/models/activity/shopActivity.ts